import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { InventoryService } from '@servicios/inventory/inventory.service';

@Component({
  selector: 'app-dashboard-inventory',
  templateUrl: './dashboard-inventory.component.html',
  styleUrls: ['./dashboard-inventory.component.css'],
  standalone: false
})
export class DashboardInventoryComponent implements OnInit {
  private service = inject(InventoryService);
  private router = inject(Router);
  activeTab = signal<number>(0);

  // KPIs
  totalItems = signal<number>(0);
  lowStock = signal<number>(0);
  alertStock = signal<number>(0);
  highStock = signal<number>(0);
  expiredCount = signal<number>(0);
  expiringSoonCount = signal<number>(0);
  anomalousCount = signal<number>(0);
  immobilizedCount = signal<number>(0);
  totalValue = signal<{ currency: string, value: number }[]>([]);

  // Charts
  itemsByTypeData = signal<any>(null);
  itemsByTypeOptions = signal<any>(null);
  
  movementsData = signal<any>(null);
  movementsOptions = signal<any>(null);

  adjustmentsData = signal<any>(null);
  adjustmentsOptions = signal<any>(null);

  globalCoverageDays = signal<number>(0);

  historicalValueData = signal<any>(null);
  historicalValueOptions = signal<any>(null);

  valuedStockByTypeData = signal<any>(null);
  valuedStockByTypeOptions = signal<any>(null);

  // Multi-Currency Selection State
  selectedHistoricalCurrency = signal<string>('USD');
  selectedValuedStockCurrency = signal<string>('USD');
  rawHistoricalValuationByCurrency = signal<{ currency: string; history: { month: string; value: number }[] }[]>([]);
  rawValuedStockByType = signal<{ type: string; currency: string; value: number }[]>([]);

  readonly availableCurrencies = computed(() => {
    const fromTotal = this.totalValue().map(v => v.currency || 'ARS');
    const fromValued = this.rawValuedStockByType().map(v => v.currency || 'ARS');
    const fromHist = this.rawHistoricalValuationByCurrency().map(v => v.currency || 'ARS');
    const set = new Set([...fromTotal, ...fromValued, ...fromHist]);
    const list = Array.from(set).filter(Boolean);
    return list.length > 0 ? list : ['ARS'];
  });

  topMovedItems = signal<any[]>([]);
  lowStockItems = signal<any[]>([]);
  expiringItems = signal<any[]>([]);
  expiredItems = signal<any[]>([]);
  highStockItems = signal<any[]>([]);
  anomalousItems = signal<any[]>([]);
  immobilizedItems = signal<any[]>([]);
  
  loadingItems = signal<boolean>(true);
  loadingMovements = signal<boolean>(true);

  // Computed Guards for Empty States & Display
  readonly hasMovementsData = computed(() => {
    const data = this.movementsData();
    if (!data?.datasets?.[0]?.data) return false;
    const counts: number[] = data.datasets[0].data;
    return counts.some(c => c > 0);
  });

  readonly hasHistoricalData = computed(() => {
    const data = this.historicalValueData();
    if (!data?.datasets?.[0]?.data) return false;
    const values: number[] = data.datasets[0].data;
    return values.some(v => v > 0);
  });

  readonly hasItemsByTypeData = computed(() => {
    const data = this.itemsByTypeData();
    if (!data?.datasets?.[0]?.data) return false;
    const values: number[] = data.datasets[0].data;
    return values.some(v => v > 0);
  });

  readonly hasValuedStockData = computed(() => {
    const data = this.valuedStockByTypeData();
    if (!data?.datasets?.[0]?.data) return false;
    const values: number[] = data.datasets[0].data;
    return values.some(v => v > 0);
  });

  readonly coverageTitle = computed(() => {
    const days = this.globalCoverageDays();
    if (days > 0) return `${days} días`;
    if (this.totalItems() > 0) return 'Sin consumo';
    return '0 días';
  });

  readonly coverageSublabel = computed(() => {
    const days = this.globalCoverageDays();
    if (days > 0) return 'Operación asegurada';
    if (this.totalItems() > 0) return 'Sin egresos en 30 días';
    return 'Sin existencias';
  });

  formatCurrencyValue(value: number, currency: string = 'ARS'): string {
    const curr = currency || 'ARS';
    try {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    } catch {
      return `${curr} $ ${(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
    }
  }

  ngOnInit() {
    this.loadItemStats();
    this.loadMovementStats();
    this.initChartOptions();
  }

  async loadItemStats() {
    this.loadingItems.set(true);
    try {
      const itemStats = await this.service.getStatistics('items');
      this.totalItems.set(itemStats.totalItems);
      this.lowStock.set(itemStats.lowStock);
      this.alertStock.set(itemStats.alertStock);
      this.highStock.set(itemStats.highStock);
      this.expiredCount.set(itemStats.expiredCount || 0);
      this.expiringSoonCount.set(itemStats.expiringSoonCount || 0);
      this.anomalousCount.set(itemStats.anomalousCount || 0);
      this.immobilizedCount.set(itemStats.immobilizedCount || 0);
      this.totalValue.set(itemStats.totalValue);
      this.rawValuedStockByType.set(itemStats.valuedStockByType || []);

      const currencies = this.availableCurrencies();
      const defaultCurr = currencies.includes('USD') ? 'USD' : (currencies[0] || 'ARS');
      this.selectedValuedStockCurrency.set(defaultCurr);

      this.initItemsByTypeChart(itemStats.itemsByType);
      this.initValuedStockByTypeChart(this.rawValuedStockByType(), defaultCurr);

      // Load detailed lists in parallel
      const [low, expiring, expired, high, anomalous, immobilized] = await Promise.all([
          this.service.getItemsByStatus('low'),
          this.service.getItemsByStatus('expiring'),
          this.service.getItemsByStatus('expired'),
          this.service.getItemsByStatus('high'),
          this.service.getItemsByStatus('anomalous'),
          this.service.getItemsByStatus('immobilized')
      ]);
      this.lowStockItems.set(low || []);
      this.expiringItems.set(expiring || []);
      this.expiredItems.set(expired || []);
      this.highStockItems.set(high || []);
      this.anomalousItems.set(anomalous || []);
      this.immobilizedItems.set(immobilized || []);

    } catch (error) {
      console.error('Error loading item stats', error);
    } finally {
      this.loadingItems.set(false);
    }
  }

  async loadMovementStats() {
    this.loadingMovements.set(true);
    try {
      const movementStats = await this.service.getStockMovementStatistics();
      this.topMovedItems.set(movementStats.topMovedItems);
      this.globalCoverageDays.set(movementStats.globalCoverageDays || 0);

      const histByCurr = movementStats.historicalValuationByCurrency || 
        (movementStats.historicalValuation ? [{ currency: 'ARS', history: movementStats.historicalValuation }] : []);
      this.rawHistoricalValuationByCurrency.set(histByCurr);

      const defaultCurr = histByCurr.some((h: any) => h.currency === 'USD') ? 'USD' : (histByCurr[0]?.currency || 'ARS');
      this.selectedHistoricalCurrency.set(defaultCurr);

      this.initMovementsChart(movementStats.movementsLast30Days);
      this.initAdjustmentsChart(movementStats.adjustmentsByReason || []);
      this.initHistoricalValueChart(histByCurr, defaultCurr);
    } catch (error) {
      console.error('Error loading movement stats', error);
    } finally {
      this.loadingMovements.set(false);
    }
  }

  setHistoricalCurrency(currency: string) {
    this.selectedHistoricalCurrency.set(currency);
    this.initHistoricalValueChart(this.rawHistoricalValuationByCurrency(), currency);
  }

  setValuedStockCurrency(currency: string) {
    this.selectedValuedStockCurrency.set(currency);
    this.initValuedStockByTypeChart(this.rawValuedStockByType(), currency);
  }

  mapItemType(type: string): string {
    const types: { [key: string]: string } = {
        'PRODUCT': 'Producto',
        'SERVICE': 'Servicio',
        'CONSUMABLE': 'Consumible',
        'STANDARD': 'Estándar',
        'REACTIVE': 'Reactivo',
        'SPARE_PART': 'Repuesto',
        'TEST_ITEM': 'Ítem de Ensayo'
    };
    return types[type] || type;
  }

  initItemsByTypeChart(data: any[]) {
    const safeData = data || [];
    const labels = safeData.map(d => this.mapItemType(d.type));
    const values = safeData.map(d => d.count);
    const backgroundColors = [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
    ];

    this.itemsByTypeData.set({
      labels: labels,
      datasets: [
        {
          data: values,
          backgroundColor: backgroundColors,
          hoverBackgroundColor: backgroundColors
        }
      ]
    });
  }

  initHistoricalValueChart(data: any[], currency: string = 'ARS') {
      const safeData = data || [];
      let history: { month: string; value: number }[] = [];

      if (safeData.length > 0 && 'history' in safeData[0]) {
        const found = safeData.find((d: any) => (d.currency || 'ARS') === currency);
        history = found ? found.history : (safeData[0]?.history || []);
      } else {
        history = safeData;
      }

      const labels = history.map(d => d.month);
      const values = history.map(d => d.value);

      this.historicalValueData.set({
          labels: labels,
          datasets: [
              {
                  label: `Inversión en Stock (${currency})`,
                  data: values,
                  fill: true,
                  borderColor: currency === 'USD' ? '#10B981' : '#36A2EB',
                  backgroundColor: currency === 'USD' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(54, 162, 235, 0.2)',
                  tension: 0.4
              }
          ]
      });

      this.updateHistoricalOptions(currency);
  }

  initMovementsChart(data: any[]) {
      const safeData = data || [];
      const types = ['IN', 'OUT', 'ADJUSTMENT'];
      const counts = types.map(t => {
          const found = safeData.find(d => d.type === t);
          return found ? found.count : 0;
      });

      this.movementsData.set({
          labels: ['Entradas', 'Salidas', 'Ajustes'],
          datasets: [
              {
                  label: 'Movimientos (Últimos 30 días)',
                  data: counts,
                  backgroundColor: ['#4BC0C0', '#FF6384', '#FFCE56'],
                  borderColor: ['#36A2EB', '#FF6384', '#FFCE56'],
                  borderWidth: 1
              }
          ]
      });
  }

  initAdjustmentsChart(data: any[]) {
      const safeData = data || [];
      const labels = safeData.map(d => d.reason);
      const values = safeData.map(d => d.count);
      const backgroundColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];

      this.adjustmentsData.set({
          labels: labels,
          datasets: [
              {
                  label: 'Ajustes por Motivo',
                  data: values,
                  backgroundColor: backgroundColors,
                  hoverBackgroundColor: backgroundColors
              }
          ]
      });
  }

  initValuedStockByTypeChart(data: any[], currency: string = 'ARS') {
    const safeData = data || [];
    const filtered = safeData.filter(d => !d.currency || d.currency === currency);
    const labels = filtered.map(d => this.mapItemType(d.type));
    const values = filtered.map(d => d.value);
    const backgroundColors = [
        '#4BC0C0', '#FF6384', '#FFCE56', '#36A2EB', '#9966FF', '#FF9F40'
    ];

    this.valuedStockByTypeData.set({
      labels: labels,
      datasets: [
        {
          label: `Valor de Stock (${currency})`,
          data: values,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors,
          borderWidth: 1
        }
      ]
    });

    this.updateValuedStockByTypeOptions(currency);
  }

  initChartOptions() {
      const documentStyle = getComputedStyle(document.documentElement);
      const textColor = documentStyle.getPropertyValue('--text-color');
      const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
      const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

      this.itemsByTypeOptions.set({
          plugins: {
              legend: {
                  labels: {
                      usePointStyle: true,
                      color: textColor
                  }
              }
          }
      });

      this.movementsOptions.set({
          plugins: {
              legend: {
                  labels: {
                      color: textColor
                  }
              }
          },
          scales: {
              y: {
                  beginAtZero: true,
                  ticks: {
                      color: textColorSecondary
                  },
                  grid: {
                      color: surfaceBorder,
                      drawBorder: false
                  }
              },
              x: {
                  ticks: {
                      color: textColorSecondary
                  },
                  grid: {
                      color: surfaceBorder,
                      drawBorder: false
                  }
              }
          }
      });

      this.adjustmentsOptions.set({
          plugins: {
              legend: {
                  position: 'right',
                  labels: { color: textColor }
              }
          },
          maintainAspectRatio: false
      });

      this.updateHistoricalOptions(this.selectedHistoricalCurrency());
      this.updateValuedStockByTypeOptions(this.selectedValuedStockCurrency());
  }

  updateHistoricalOptions(currency: string = 'ARS') {
      const documentStyle = getComputedStyle(document.documentElement);
      const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
      const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

      this.historicalValueOptions.set({
          plugins: {
              legend: {
                  display: false
              },
              tooltip: {
                callbacks: {
                    label: (context: any) => {
                        return this.formatCurrencyValue(context.parsed.y, currency);
                    }
                }
              }
          },
          scales: {
              y: {
                  beginAtZero: false,
                  ticks: {
                      color: textColorSecondary,
                      callback: (value: any) => {
                        return this.formatCurrencyValue(value as number, currency);
                      }
                  },
                  grid: {
                      color: surfaceBorder
                  }
              },
              x: {
                  ticks: {
                      color: textColorSecondary
                  }
              }
          }
      });
  }

  updateValuedStockByTypeOptions(currency: string = 'ARS') {
      const documentStyle = getComputedStyle(document.documentElement);
      const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
      const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

      this.valuedStockByTypeOptions.set({
          indexAxis: 'y',
          plugins: {
              legend: {
                  display: false
              },
              tooltip: {
                callbacks: {
                    label: (context: any) => {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.x !== null) {
                            label += this.formatCurrencyValue(context.parsed.x, currency);
                        }
                        return label;
                    }
                }
              }
          },
          scales: {
              x: {
                  ticks: {
                      color: textColorSecondary,
                      callback: (value: any) => {
                        return this.formatCurrencyValue(value as number, currency);
                      }
                  },
                  grid: {
                      color: surfaceBorder,
                      drawBorder: false
                  }
              },
              y: {
                  ticks: {
                      color: textColorSecondary
                  },
                  grid: {
                      color: surfaceBorder,
                      drawBorder: false
                  }
              }
          }
      });
  }

  selectTab(index: number) {
    this.activeTab.set(index);
    // Optional: Smooth scroll to the table
    const tableElement = document.getElementById('inventory-detail-table');
    if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  goToItem(id: string) {
    if (!id) return;
    this.router.navigate(['/inventory/items', id]);
  }
}
